/**
 * Thin wrapper around amazon-cognito-identity-js so the rest of the app
 * works with Promises instead of node-style callbacks.
 *
 * Refresh-token TTL is configured at the User Pool / App Client level
 * (see infra/provision-cognito.sh — RefreshToken 7 days). When the
 * refresh token expires, getSession() fails and AuthContext signs the
 * user out — which is the "refresh login once a week" requirement.
 */
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';

const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
const ClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string;

if (!UserPoolId || !ClientId) {
  // Surface misconfig early — easier than debugging silent auth failures.
  // eslint-disable-next-line no-console
  console.warn('Cognito env vars missing. Did you run infra/provision-cognito.sh?');
}

export const userPool = new CognitoUserPool({ UserPoolId, ClientId });

export type Kid = { name: string; yearOfBirth: number };

export type SignUpInput = {
  email: string;
  password: string;
  parentName: string;
  phoneNumber: string; // E.164 e.g. +14165551234
};

export async function signUp(input: SignUpInput): Promise<ISignUpResult> {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: input.email }),
    new CognitoUserAttribute({ Name: 'phone_number', Value: input.phoneNumber }),
    new CognitoUserAttribute({ Name: 'custom:parent_name', Value: input.parentName }),
  ];
  return new Promise((resolve, reject) => {
    userPool.signUp(input.email, input.password, attributes, [], (err, result) => {
      if (err || !result) return reject(err ?? new Error('Sign up failed'));
      resolve(result);
    });
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export async function resendCode(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err) => (err ? reject(err) : resolve()));
  });
}

export async function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  const auth = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve(session),
      onFailure: (err) => reject(err),
    });
  });
}

/**
 * Reads the cached session and silently refreshes the access/id tokens
 * if needed. If the refresh token itself has expired (>7 days idle),
 * getSession returns an error — caller should treat that as signed out.
 */
export async function getCurrentSession(): Promise<CognitoUserSession | null> {
  const user = userPool.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session);
    });
  });
}

export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

/**
 * Kicks off the forgot-password flow. Cognito emails a 6-digit code to the
 * verified email on file. To finish the flow, call `confirmForgotPassword`
 * with that code and the new password.
 */
export async function forgotPassword(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export async function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}
